import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import {
  AuthenticationProvider,
  CountryDetection,
  DatadogLogsRouteTracker,
  DatadogRUMRouteTracker,
  DisableAddToHomeScreen,
  DiscountsRefresh,
  DiscountStickyBar,
  SearchResultQuestionnaire,
  SessionSharing,
  SlideInWebCouponBanner,
  SyncSessionBetweenTabs,
  useFullScreenView,
  InitializeAbTestsConditionally,
  SubscribeToWhatsappNewsletter,
  CheckoutPreloadConditional,
  FraudDetection,
  useShowSlideInWebCouponBanner,
  ActivateWebCouponInOutdatedBasket,
  NotificationProvider,
  BrowserMemoryConsumptionTracker,
} from '@aboutyou/logic-components';
import {TrackCustomer, TrackSearch} from '@aboutyou/tracking';
import {
  ClientGate,
  ErrorBoundary,
  ScrollToTop,
  SkipToContentLink,
  useHydrationDone,
} from '@aboutyou/ui-components';
import {AsyncPageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/AsyncPageTrackingContext';
import React, {useRef} from 'react';
import {PhonePopupConditional} from '../bridge/global/PhonePopup/PhonePopup';
import {SettingsModal} from '../bridge/global/SettingsModal/SettingsModal';
import {ToastNotifications} from '../bridge/global/ToastNotifications/ToastNotifications';
import {UpdateCampaigns} from '../bridge/global/UpdateCampaigns/UpdateCampaigns';
import {Header} from './components/globals/Header/Header';
import {RouteComponents} from './RouteComponents';
import {Footer} from './components/globals/Footer';
import {
  ConditionalSyncLegacyState,
  LegacyStateProvider,
} from '../bridge/SyncLegacyStateContext';
import {
  useHistory,
  useIsBasketPage,
  useIsOrderSuccessPage,
} from '@aboutyou/router';
import {
  LegacyAuthSyncProvider,
  useHandleOnSyncLegacy,
} from '../bridge/LegacyAuthSyncProvider';

export const App: React.FC = () => {
  return (
    <LegacyAuthSyncProvider>
      <Content />
    </LegacyAuthSyncProvider>
  );
};

const Content: React.FC = () => {
  // A manual call to sync leagacy on desktop is required in favor of using the
  // bi-directional syncs from the sync-state-hooks functions
  const handleOnSyncLegacy = useHandleOnSyncLegacy();
  return (
    <AsyncPageTrackingContextProvider>
      <DisableAddToHomeScreen />
      <LegacyStateProvider>
        <SkipToContentLink />
        <DiscountsRefresh />
        <NotificationProvider>
          <AuthenticationProvider onSyncLegacy={handleOnSyncLegacy}>
            <ConditionalHeader />
            <ErrorBoundary renderContext="route_components">
              <RouteComponents />
            </ErrorBoundary>
            <ConditionalFooter />

            <ClientGate>
              <SyncSessionBetweenTabs />
              <DiscountStickyBar />
              <ErrorBoundary
                renderContext="country_detection"
                ErrorComponent={null}
              >
                <CountryDetectionConditionally />
              </ErrorBoundary>
              <ErrorBoundary
                renderContext="search_results_questionnaire"
                ErrorComponent={null}
              >
                <SearchResultQuestionnaire />
              </ErrorBoundary>

              <ErrorBoundary
                renderContext="web_coupon_banner"
                ErrorComponent={null}
              >
                <ConditionalSlideInWebCouponBanner />
              </ErrorBoundary>

              <ErrorBoundary renderContext="scroll_top" ErrorComponent={null}>
                <ScrollToTop position="right" />
              </ErrorBoundary>

              <ErrorBoundary
                renderContext="track_customer"
                ErrorComponent={null}
              >
                <TrackCustomer />
              </ErrorBoundary>

              <ErrorBoundary renderContext="track_search" ErrorComponent={null}>
                <TrackSearch />
              </ErrorBoundary>
              <ConditionalPhonePopup />

              <ErrorBoundary
                renderContext="session_sharing"
                ErrorComponent={null}
              >
                <SessionSharing />
              </ErrorBoundary>
              <ErrorBoundary
                renderContext="legacy_state_sync"
                ErrorComponent={null}
              >
                <ConditionalSyncLegacyState>
                  {({mountLegacyComponents}) => {
                    return mountLegacyComponents ? (
                      <>
                        <SettingsModal />
                        <ToastNotifications />
                        <UpdateCampaigns />
                      </>
                    ) : null;
                  }}
                </ConditionalSyncLegacyState>
              </ErrorBoundary>
              <ErrorBoundary
                renderContext="activate_web_coupon_in_outdated_basket"
                ErrorComponent={null}
              >
                <ActivateWebCouponInOutdatedBasket />
              </ErrorBoundary>
              <DatadogRUMRouteTracker />
              <ConditionalFraudDetection />
              <DatadogLogsRouteTracker />
              <BrowserMemoryConsumptionTracker />
              <InitializeAbTestsConditionally />
              <SubscribeToWhatsappNewsletter />
              <CheckoutPreloadConditional />
            </ClientGate>
          </AuthenticationProvider>
        </NotificationProvider>
      </LegacyStateProvider>
    </AsyncPageTrackingContextProvider>
  );
};

const ConditionalFraudDetection = () => {
  const isHydrationDone = useHydrationDone();

  // We need the script injection to only happen after hydration to get the
  // real cookie value
  if (!isHydrationDone) {
    return null;
  }

  return <FraudDetection />;
};

const ConditionalPhonePopup = () => {
  const isHydrationDone = useHydrationDone();
  const isBasketPage = useIsBasketPage();
  if (!isHydrationDone || isBasketPage) {
    return null;
  }
  return <PhonePopupConditional />;
};

const ConditionalHeader = () => {
  const {isHeaderDisabled} = useFullScreenView();
  if (isHeaderDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="header">
      <Header />
    </ErrorBoundary>
  );
};

const ConditionalFooter = () => {
  const {isFooterDisabled} = useFullScreenView();
  if (isFooterDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="footer">
      <Footer />
    </ErrorBoundary>
  );
};

const CountryDetectionConditionally = () => {
  const isInternationalPopupEnabled = useIsSoftFeatureToggleActive(
    'show_international_popup',
  );

  const history = useHistory();
  const params = new URLSearchParams(history.location.search);

  const isDisabledByUrlParam = useRef(
    params.get('disableCountrySwitch') === '1',
  ).current;

  const enteredThroughOSP = useRef(useIsOrderSuccessPage()).current;

  return isInternationalPopupEnabled &&
    !enteredThroughOSP &&
    !isDisabledByUrlParam ? (
    <CountryDetection />
  ) : null;
};

const ConditionalSlideInWebCouponBanner = () => {
  const shouldShowSlideInWebCouponBanner = useShowSlideInWebCouponBanner();
  return shouldShowSlideInWebCouponBanner ? <SlideInWebCouponBanner /> : null;
};
